import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import TherapistSideBar from "../../common/TherapistSidebar/TherapistSidebar";
import TherapistAppointments from "./TherapistAppointments";
import { useTranslation } from "react-i18next";
import { 
  createStripeAccount, 
  getStripeOnboardingLink,
  clearError,
  clearStripeError,
} from "../../redux/therapistSlice";
import { getTherapistRequests } from "../../redux/requestSlice";
import { getMyAvailabilities } from "../../redux/availabilitySlice";
import TherapistStatsOverview from "./TherapistStatsOverview";

const PAID_STATUSES = new Set(["success", "succeeded", "paid", "completed"]);
const ACTIVE_STATUSES = new Set(["pending", "confirmed", "accepted"]);
const CANCELLED_STATUSES = new Set(["cancelled", "canceled"]);

const normalize = (value) => String(value || "").toLowerCase();

const parseDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getRequestDate = (request) => {
  const selectedSlot =
    request?.selectedSlot && typeof request.selectedSlot === "object"
      ? request.selectedSlot
      : null;

  const rawDate =
    selectedSlot?.startDate ??
    selectedSlot?.startTime ??
    request?.sessionDate ??
    request?.startDate ??
    request?.startTime ??
    request?.appointmentDate ??
    request?.date ??
    request?.createdAt;

  return parseDate(rawDate);
};

const getAvailabilityDate = (slot) => {
  const rawDate =
    slot?.startDate ??
    slot?.startTime ??
    slot?.date ??
    slot?.createdAt;
  return parseDate(rawDate);
};

const isPaidRequest = (request) => {
  const paymentStatus = normalize(request?.payment?.status);
  const nestedPaymentStatus = normalize(request?.payment?.details?.status);
  return PAID_STATUSES.has(paymentStatus) || PAID_STATUSES.has(nestedPaymentStatus);
};

function TherapistPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // Redux state
  const therapistState = useSelector((state) => state.therapist) || {};
  const { 
    therapist, 
    loading, 
    stripeLoading, 
    error, 
    stripeError,
    stripeOnboardingUrl 
  } = therapistState;
  const { requests = [] } = useSelector((state) => state.requests || {});
  const { availabilities = [] } = useSelector((state) => state.availability || {});
  
  // Local state
  const [showStripeWarning, setShowStripeWarning] = useState(false);

  // Redundant fetch removed as it's handled by parent TherapistHomePage
  useEffect(() => {
    dispatch(getTherapistRequests());
  }, [dispatch]);

  useEffect(() => {
    if (therapist?._id) {
      dispatch(getMyAvailabilities(therapist._id));
    }
  }, [dispatch, therapist?._id]);

  const stats = useMemo(() => {
    const now = new Date();

    const totalSessions = availabilities.length || requests.length;

    const activeBookedSessions = requests.filter((request) => {
      const status = normalize(request?.status);
      const requestDate = getRequestDate(request);
      if (!requestDate || requestDate <= now) return false;
      return ACTIVE_STATUSES.has(status);
    }).length;

    const activeUnbookedSessions = availabilities.filter((slot) => {
      const slotDate = getAvailabilityDate(slot);
      if (!slotDate || slotDate <= now) return false;
      const status = normalize(slot?.status);
      return status === "available" && slot?.isBooked !== true;
    }).length;

    const activeSessions = activeBookedSessions + activeUnbookedSessions;

    const scheduledPaidSessions = requests.filter((request) => {
      if (!isPaidRequest(request)) return false;
      const requestDate = getRequestDate(request);
      return requestDate && requestDate > now;
    }).length;

    const completedPaidSessions = requests.filter((request) => {
      return normalize(request?.status) === "completed" && isPaidRequest(request);
    }).length;

    const cancelledSessions = requests.filter((request) =>
      CANCELLED_STATUSES.has(normalize(request?.status)),
    ).length;

    const unbookedSlots = availabilities.filter((slot) => {
      const slotDate = getAvailabilityDate(slot);
      if (!slotDate || slotDate <= now) return false;
      const status = normalize(slot?.status);
      return status === "available" && slot?.isBooked !== true;
    }).length;

    return {
      totalSessions,
      activeSessions,
      scheduledPaidSessions,
      completedPaidSessions,
      cancelledSessions,
      unbookedSlots,
    };
  }, [availabilities, requests]);

  // Stripe account check - show warning if account doesn't exist or is not fully active
  useEffect(() => {
    console.log('🔍 Therapist Data:', therapist);

    if (therapist) {
      const stripeAccount = therapist.stripeAccount;

      // Show warning if:
      // 1. No Stripe account exists
      // 2. Account is restricted
      // 3. Payouts are not enabled (even if charges are enabled)
      // 4. Details not submitted
      const needsSetup = !stripeAccount?.accountId ||
                        stripeAccount?.status === 'restricted' ||
                        !stripeAccount?.payoutsEnabled ||
                        !stripeAccount?.detailsSubmitted;

      console.log('🔍 Stripe Account Check:', {
        accountId: stripeAccount?.accountId,
        status: stripeAccount?.status,
        payoutsEnabled: stripeAccount?.payoutsEnabled,
        detailsSubmitted: stripeAccount?.detailsSubmitted,
        needsSetup,
        showStripeWarning: needsSetup
      });

      setShowStripeWarning(needsSetup);
    } else {
      console.log('❌ Therapist data is empty');
    }
  }, [therapist]);

  // Redirect when onboarding URL is received
  useEffect(() => {
    if (stripeOnboardingUrl) {
      window.location.href = stripeOnboardingUrl;
    }
  }, [stripeOnboardingUrl]);

  // Create Stripe account and handle onboarding
  const handleCreateStripeAccount = async () => {
    try {
      // Clear previous errors
      if (error) dispatch(clearError());
      if (stripeError) dispatch(clearStripeError());

      // If account exists but is incomplete, just get new onboarding link
      if (therapist?.stripeAccount?.accountId) {
        const onboardingResult = await dispatch(getStripeOnboardingLink()).unwrap();
        console.log('Onboarding link received for existing account:', onboardingResult);
      } else {
        // Create new account and get onboarding link
        const createResult = await dispatch(createStripeAccount()).unwrap();
        console.log('Stripe account created:', createResult);

        const onboardingResult = await dispatch(getStripeOnboardingLink()).unwrap();
        console.log('Onboarding link received:', onboardingResult);
      }

      // Redirect will be handled by useEffect

    } catch (error) {
      console.error('Stripe account setup error:', error);
    }
  };

  // Clear error messages
  const handleClearError = () => {
    if (error) dispatch(clearError());
    if (stripeError) dispatch(clearStripeError());
  };

  // Determine therapist name
  const therapistName = therapist ? 
    `${therapist.name || ''} ${therapist.surname || ''}`.trim() : 'Therapist';

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen notranslate" translate="no">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-lg text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Active error message
  const activeError = stripeError || error;

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      <div className="flex-shrink-0">
        <TherapistSideBar />
      </div>
      <div className="flex-1 min-w-0 p-4 lg:p-6">
        {/* Header */}
        <div className="mb-6 notranslate" translate="no">
          <h1 className="text-[15px] md:text-[32px] font-poppins text-gray-900">
            <span>{t("welcome")}</span><span>, </span><span className="font-bold">{therapistName}</span>
          </h1>
          <p className="font-poppins mt-3 md:text-[16px] text-[10px] text-gray-600">
            <span>{t("userMainPageTitle")}</span>
          </p>
        </div>
        
        {/* Stripe Account Warning */}
        {showStripeWarning && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-amber-800">
                  {therapist?.stripeAccount?.accountId ?
                    'Payment Account Incomplete' :
                    'Payment Account Setup Required'
                  }
                </h3>
                <p className="mt-2 text-sm text-amber-700">
                  {therapist?.stripeAccount?.accountId ? (
                    <>
                      Your Stripe account setup is incomplete or restricted. Please complete the verification process to receive payments.
                      {therapist?.stripeAccount?.status === 'restricted' && (
                        <span className="block mt-1 font-medium">
                          Status: Account Restricted - Additional information required
                        </span>
                      )}
                      {!therapist?.stripeAccount?.payoutsEnabled && (
                        <span className="block mt-1 font-medium">
                          Payouts are not enabled yet - Complete verification to enable
                        </span>
                      )}
                    </>
                  ) : (
                    'To receive payments from appointments, you need to set up your Stripe account. This process takes only a few minutes and is completely secure.'
                  )}
                </p>
                <div className="mt-4">
                  <button
                    onClick={handleCreateStripeAccount}
                    disabled={stripeLoading}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {stripeLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Setting up account...
                      </>
                    ) : (
                      <>
                        <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        {therapist?.stripeAccount?.accountId ?
                          'Complete Stripe Setup' :
                          'Set up Stripe Account'
                        }
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {activeError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800">
                    Operation Error
                  </h3>
                  <p className="mt-1 text-sm text-red-700">{activeError}</p>
                </div>
              </div>
              <button
                onClick={handleClearError}
                className="flex-shrink-0 ml-4 text-red-400 hover:text-red-600 transition-colors duration-200"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Stripe Account Success Message - Only show when fully active (no warning) */}
        {!showStripeWarning && therapist?.stripeAccount?.accountId && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  Payment Account Active
                </h3>
                <p className="mt-1 text-sm text-green-700">
                  Your Stripe account has been successfully set up. You can now receive payments from appointments.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Main Content */}
        <TherapistStatsOverview stats={stats} />

        <div className="bg-white rounded-lg shadow-sm">
          <TherapistAppointments />
        </div>
      </div>
    </div>
  );
}

export default TherapistPage;
